import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import heroBuilding from "../assets/hero-building.png";
import industrial1 from "../assets/industrial-1.png";
import industrial2 from "../assets/industrial-2.png";

export default function Landing() {
  const { t } = useTranslation();

  return (
    <div className="bg-kag-bg text-white">
      {/* HERO */}
      <section className="bg-kag-bg">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] py-10 md:py-14 lg:py-[70px]">
          <div className="grid gap-8 md:gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <h1 className="font-clash uppercase text-white text-[34px] sm:text-[44px] md:text-[52px] lg:text-[58px] xl:text-[64px] leading-[0.98] tracking-tight">
                <span className="block font-medium">{t("landing.heroLine1")}</span>
                <span className="block font-medium">{t("landing.heroLine2")}</span>
                <span className="block font-medium">{t("landing.heroLine3")}</span>
                <span className="block font-bold">{t("landing.heroLine4")}</span>
                <span className="block font-medium">{t("landing.heroLine5")}</span>
              </h1>
              <div className="mt-6 lg:mt-8 font-clash text-[#b9b9b9] text-[14px] md:text-[15px] lg:text-[17px] leading-[1.5]">
                <p>{t("landing.heroBody1")}</p>
                <p>{t("landing.heroBody2")}</p>
                <p>{t("landing.heroBody3")}</p>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/who-we-are">
                  <Button variant="filled">
                    {t("landing.btnWhoAreWe")}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline-light">
                    {t("common.contactUs")}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="overflow-hidden rounded-[24px] sm:rounded-[32px] lg:rounded-[45px] aspect-[563/392] w-full lg:max-w-[563px] lg:ml-auto">
                <img src={heroBuilding} alt="KAG Defense facility" className="block h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 — WHITE */}
      <Section bg="white">
        <SectionRow
          title={
            <>
              {t("landing.platformTitle1")}<br />
              {t("landing.platformTitle2")}
            </>
          }
          body={
            <>
              <p className="mb-4">{t("landing.platformBody1")}</p>
              <p>{t("landing.platformBody2")}</p>
            </>
          }
          theme="light"
        />
      </Section>

      {/* SECTION 2 — DARK — What defines our approach? */}
      <Section bg="dark">
        <div>
          <h2 className="font-clash font-semibold text-white text-[28px] sm:text-[34px] md:text-[40px] lg:text-[46px] leading-[1] tracking-tight">
            {t("landing.approachTitle")}
          </h2>
          <div className="mt-7 grid gap-7 md:grid-cols-3 md:gap-8">
            <ApproachItem title={t("landing.discipline")} body={t("landing.disciplineBody")} />
            <ApproachItem title={t("landing.governance")} body={t("landing.governanceBody")} />
            <ApproachItem title={t("landing.responsibility")} body={t("landing.responsibilityBody")} />
          </div>
        </div>
      </Section>

      {/* SECTION 3 — WHITE — What we are Building? */}
      <Section bg="white">
        <SectionRow
          title={t("landing.buildingTitle")}
          body={<p>{t("landing.buildingBody")}</p>}
          cta={
            <Link to="/what-we-do">
              <Button variant="outline-dark">{t("common.learnMore")}</Button>
            </Link>
          }
          theme="light"
        />
      </Section>

      {/* SECTION 4 — DARK — Governance from the Beginning */}
      <Section bg="dark">
        <SectionRow
          title={
            <>
              {t("landing.governanceFromTitle1")}<br />
              {t("landing.governanceFromTitle2")}
            </>
          }
          body={<p>{t("landing.governanceFromBody")}</p>}
          cta={
            <Link to="/governance">
              <Button variant="outline-light">{t("common.viewGovernance")}</Button>
            </Link>
          }
          theme="dark"
        />
      </Section>

      {/* SECTION 5 — WHITE — Contact + 2 image cards */}
      <section id="contact" className="bg-white text-black">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] py-10 md:py-14 lg:py-[60px]">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div>
              <div>
                <Link to="/contact">
                  <Button variant="outline-dark">{t("common.contactUs")}</Button>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5">
              <div className="overflow-hidden rounded-[18px] lg:rounded-[27px] aspect-[254/230]">
                <img src={industrial1} alt="" className="block h-full w-full object-cover" />
              </div>
              <div className="overflow-hidden rounded-[18px] lg:rounded-[27px] aspect-[254/230]">
                <img src={industrial2} alt="" className="block h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — DARK — Final tagline */}
      <section className="bg-kag-bg">
        <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] py-12 md:py-16 lg:py-[80px]">
          <div className="text-center font-clash text-white text-[28px] sm:text-[36px] md:text-[44px] lg:text-[52px] leading-[1.15] tracking-tight space-y-2 lg:space-y-3">
            <p>
              <span className="font-normal">{t("landing.tagline1Pre")} </span>
              <span className="font-medium">{t("landing.tagline1Mid")}</span>
              <span className="font-normal"> {t("landing.tagline1Post")}</span>
            </p>
            <p>
              <span className="font-normal">{t("landing.tagline2Pre")} </span>
              <span className="font-medium">{t("landing.tagline2Mid")}</span>
              <span className="font-normal">.</span>
            </p>
            <p>
              <span className="font-normal">{t("landing.tagline3Pre")} </span>
              <span className="font-medium">{t("landing.tagline3Mid")}</span>
              <span className="font-normal">.</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ bg, children }: { bg: "dark" | "white"; children: React.ReactNode }) {
  const bgClass = bg === "dark" ? "bg-kag-bg text-white" : "bg-white text-black";
  return (
    <section className={bgClass}>
      <div className="mx-auto w-full max-w-page px-5 sm:px-8 md:px-10 lg:px-[60px] py-10 md:py-14 lg:py-[64px]">
        {children}
      </div>
    </section>
  );
}

function SectionRow({
  title,
  body,
  cta,
  theme,
}: {
  title: React.ReactNode;
  body: React.ReactNode;
  cta?: React.ReactNode;
  theme: "dark" | "light";
}) {
  const titleColor = theme === "dark" ? "text-white" : "text-black";
  const bodyColor = "text-[#8e8e8e]";
  return (
    <div className="grid gap-6 lg:grid-cols-12 lg:gap-12 lg:items-end">
      <div className="lg:col-span-9">
        <h2 className={`font-clash font-semibold ${titleColor} text-[28px] sm:text-[34px] md:text-[40px] lg:text-[46px] leading-[1] tracking-tight`}>
          {title}
        </h2>
        <div className={`mt-5 lg:mt-6 font-clash font-medium ${bodyColor} text-[14px] lg:text-[15px] leading-[1.55] max-w-[760px] space-y-3`}>
          {body}
        </div>
      </div>
      {cta && <div className="lg:col-span-3 lg:flex lg:items-end lg:justify-end">{cta}</div>}
    </div>
  );
}

function ApproachItem({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="block w-[10px] h-[10px] rounded-full bg-kag-red shrink-0" />
        <h3 className="font-clash font-semibold text-white text-[16px] lg:text-[18px] tracking-[0.02em] uppercase">
          {title}
        </h3>
      </div>
      <p className="mt-3 font-clash font-medium text-[#b9b9b9] text-[13px] lg:text-[14px] leading-[1.55]">
        {body}
      </p>
    </div>
  );
}
